import { clsx } from 'clsx'
import {
  Download,
  FileText,
  FolderOpen,
  Highlighter,
  LayoutGrid,
  Lock,
  MonitorSmartphone,
  Moon,
  PenLine,
  Signature,
  Sun,
  TextCursorInput,
  Trash2,
  WifiOff,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'
import { openDocument, openFromPicker, openRecent } from '@/lib/commands'
import { formatBytes } from '@/lib/files'
import { deleteSession, listRecents, type RecentMeta } from '@/lib/storage'
import { useUi } from '@/store/uiStore'
import { Logo } from '../layout/TopBar'
import { Button, IconButton } from '../ui/Button'

const FEATURES = [
  {
    icon: TextCursorInput,
    title: 'Edit text',
    body: 'Click any line to change the words — font and color matched.',
  },
  {
    icon: Highlighter,
    title: 'Mark up',
    body: 'Highlight, underline, draw, shapes, arrows and sticky notes.',
  },
  {
    icon: Signature,
    title: 'Fill & sign',
    body: 'Fill forms, draw or type a signature and reuse it anywhere.',
  },
  { icon: LayoutGrid, title: 'Organize', body: 'Reorder, rotate, delete, insert, merge and extract pages.' },
  {
    icon: WifiOff,
    title: 'Works offline',
    body: 'Install it like an app. No account, no upload, no waiting.',
  },
  { icon: Lock, title: 'Private', body: 'Your files never leave your device. Everything runs locally.' },
]

function timeAgo(t: number) {
  const s = (Date.now() - t) / 1000
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)} min ago`
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`
  if (s < 86400 * 7) return `${Math.floor(s / 86400)} d ago`
  return new Date(t).toLocaleDateString()
}

export function Home() {
  const [recents, setRecents] = useState<RecentMeta[] | null>(null)
  const { canInstall, install } = useInstallPrompt()
  const theme = useUi((s) => s.theme)

  useEffect(() => {
    listRecents()
      .then(setRecents)
      .catch(() => setRecents([]))
  }, [])

  const trySample = async () => {
    const { createSamplePdf } = await import('@/lib/sample')
    await openDocument(await createSamplePdf(), 'Welcome to Folio.pdf')
  }

  const dark = theme === 'dark' || (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches)

  return (
    <div className="safe-top h-full overflow-y-auto">
      <div className="mx-auto flex min-h-full max-w-5xl flex-col px-5 pb-10">
        <header className="flex h-16 items-center gap-2.5">
          <Logo />
          <span className="font-semibold text-[17px] tracking-tight">Folio</span>
          <div className="ml-auto flex items-center gap-1">
            {canInstall && (
              <Button size="sm" variant="ghost" icon={<Download className="size-4" />} onClick={install}>
                Install app
              </Button>
            )}
            <IconButton
              label={dark ? 'Light mode' : 'Dark mode'}
              onClick={() => useUi.getState().setTheme(dark ? 'light' : 'dark')}
            >
              {dark ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
            </IconButton>
          </div>
        </header>

        <section className="flex flex-col items-center pt-10 pb-12 text-center sm:pt-16">
          <h1 className="max-w-2xl text-balance font-semibold text-[34px] leading-[1.1] tracking-tight sm:text-5xl">
            The PDF editor that{' '}
            <span className="bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">
              gets out of your way
            </span>
          </h1>
          <p className="mt-4 max-w-xl text-balance text-fg-2 sm:text-lg">
            Read, highlight, edit text, fill forms, sign and organize pages. Fast, private and offline — on
            your phone, tablet or desktop.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              variant="primary"
              size="lg"
              icon={<FolderOpen className="size-5" />}
              onClick={() => void openFromPicker()}
            >
              Open a PDF
            </Button>
            <Button size="lg" icon={<FileText className="size-5" />} onClick={() => void trySample()}>
              Try a sample
            </Button>
          </div>
          <p className="mt-4 flex items-center gap-2 text-fg-3 text-sm">
            <MonitorSmartphone className="size-4" /> or drop a file anywhere on this window
          </p>
        </section>

        {recents && recents.length > 0 && (
          <section className="mb-12">
            <h2 className="mb-3 font-semibold text-fg-2 text-sm">Recent</h2>
            <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {recents.map((r) => (
                <li key={r.id} className="group relative">
                  <button
                    type="button"
                    onClick={() => void openRecent(r.id)}
                    className="flex w-full flex-col overflow-hidden rounded-2xl border border-line bg-surface text-left shadow-sm transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="flex aspect-[4/3] items-start justify-center overflow-hidden bg-surface-3 px-4 pt-4">
                      {r.thumb ? (
                        <img
                          src={r.thumb}
                          alt=""
                          className="w-full rounded-t-[3px] bg-white object-cover object-top shadow-sm"
                        />
                      ) : (
                        <FileText className="mt-6 size-10 text-fg-3" />
                      )}
                    </div>
                    <div className="border-line border-t px-3 py-2.5">
                      <div className="truncate font-medium text-[13px]" title={r.name}>
                        {r.name.replace(/\.pdf$/i, '')}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-fg-3">
                        <span>{timeAgo(r.updatedAt)}</span>
                        <span>·</span>
                        <span>
                          {r.pageCount} {r.pageCount === 1 ? 'page' : 'pages'}
                        </span>
                        {r.edited && (
                          <span className="ml-auto flex items-center gap-1 text-accent">
                            <PenLine className="size-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${r.name} from recents`}
                    onClick={async () => {
                      await deleteSession(r.id)
                      setRecents((list) => list?.filter((x) => x.id !== r.id) ?? null)
                    }}
                    className="absolute top-2 right-2 flex size-8 items-center justify-center rounded-xl bg-surface/90 text-fg-3 opacity-0 shadow-sm transition-opacity hover:text-danger group-hover:opacity-100 max-md:opacity-100"
                  >
                    <Trash2 className="size-4" />
                  </button>
                  <span className="sr-only">{formatBytes(r.size)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section
          className={clsx('grid gap-3 sm:grid-cols-2 lg:grid-cols-3', recents?.length ? '' : 'mt-auto')}
        >
          {FEATURES.map((f) => (
            <div key={f.title} className="flex gap-3.5 rounded-2xl border border-line bg-surface p-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
                <f.icon className="size-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">{f.title}</h3>
                <p className="mt-0.5 text-[13px] text-fg-2 leading-relaxed">{f.body}</p>
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  )
}
